<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

const templates = useMapGetter('documentTemplates/getDocumentTemplates');
const uiFlags = useMapGetter('documentTemplates/getUIFlags');
const isLoading = computed(() => uiFlags.value.isFetching);

const search = ref('');
const openMenuId = ref(null);

const showPreview = ref(false);
const previewName = ref('');
const previewHtml = ref('');
const previewLoading = ref(false);

onMounted(() => {
  store.dispatch('documentTemplates/get');
});

const filteredTemplates = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return templates.value;
  return templates.value.filter(template =>
    template.name.toLowerCase().includes(term)
  );
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
function formatDate(value) {
  if (!value) return '';
  return dateFormatter.format(new Date(value));
}

function menuItems() {
  return [
    {
      label: t('DOCUMENT_TEMPLATES.ACTIONS.EDIT'),
      value: 'edit',
      action: 'edit',
      icon: 'i-lucide-pencil',
    },
    {
      label: t('DOCUMENT_TEMPLATES.ACTIONS.PREVIEW'),
      value: 'preview',
      action: 'preview',
      icon: 'i-lucide-eye',
    },
    {
      label: t('DOCUMENT_TEMPLATES.ACTIONS.GENERATE'),
      value: 'generate',
      action: 'generate',
      icon: 'i-lucide-file-down',
    },
    {
      label: t('DOCUMENT_TEMPLATES.ACTIONS.DELETE'),
      value: 'delete',
      action: 'delete',
      icon: 'i-lucide-trash-2',
    },
  ];
}

function toggleMenu(id) {
  openMenuId.value = openMenuId.value === id ? null : id;
}

function goToNew() {
  router.push({
    name: 'documents_settings_new',
    params: { accountId: accountId.value },
  });
}

function goToEdit(id) {
  router.push({
    name: 'documents_settings_edit',
    params: { accountId: accountId.value, templateId: String(id) },
  });
}

async function deleteTemplate(id) {
  try {
    await store.dispatch('documentTemplates/delete', id);
    useAlert(t('DOCUMENT_TEMPLATES.API.DELETE_SUCCESS'));
  } catch {
    useAlert(t('DOCUMENT_TEMPLATES.API.ERROR'));
  }
}

async function previewTemplate(template) {
  showPreview.value = true;
  previewName.value = template.name;
  previewLoading.value = true;
  previewHtml.value = '';
  try {
    previewHtml.value = await store.dispatch('documentTemplates/preview', {
      id: template.id,
    });
  } catch {
    useAlert(t('DOCUMENT_TEMPLATES.API.ERROR'));
    showPreview.value = false;
  } finally {
    previewLoading.value = false;
  }
}

async function generateTemplate(id) {
  try {
    const doc = await store.dispatch('documentTemplates/generate', { id });
    useAlert(t('DOCUMENT_TEMPLATES.GENERATE.SUCCESS'));
    if (doc.pdf_url) window.open(doc.pdf_url, '_blank');
  } catch {
    useAlert(t('DOCUMENT_TEMPLATES.GENERATE.ERROR'));
  }
}

function handleAction(template, { action }) {
  openMenuId.value = null;
  if (action === 'edit') goToEdit(template.id);
  else if (action === 'preview') previewTemplate(template);
  else if (action === 'generate') generateTemplate(template.id);
  else if (action === 'delete') deleteTemplate(template.id);
}
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('DOCUMENT_TEMPLATES.LOADING')"
    :no-records-found="!templates.length"
    :no-records-message="t('DOCUMENT_TEMPLATES.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('DOCUMENT_TEMPLATES.HEADER')"
        :description="t('DOCUMENT_TEMPLATES.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            :label="t('DOCUMENT_TEMPLATES.NEW')"
            @click="goToNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-5">
        <!-- Search -->
        <div class="relative max-w-sm">
          <span
            class="absolute -translate-y-1/2 i-lucide-search size-4 left-3 top-1/2 text-n-slate-10"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="t('DOCUMENT_TEMPLATES.SEARCH')"
            class="py-2 pr-3 pl-9 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
        </div>

        <!-- Card grid -->
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="flex flex-col p-4 rounded-xl border transition-shadow cursor-pointer group border-n-weak bg-n-solid-1 hover:shadow-md"
            @click="goToEdit(template.id)"
          >
            <div class="flex gap-2 justify-between items-start">
              <div class="flex gap-2.5 items-start min-w-0">
                <span
                  class="flex justify-center items-center rounded-lg size-9 bg-n-blue-3 text-n-blue-11 shrink-0"
                >
                  <span class="i-lucide-file-signature size-5" />
                </span>
                <div class="min-w-0">
                  <h3
                    class="text-sm font-medium truncate text-n-slate-12 line-clamp-1"
                  >
                    {{ template.name }}
                  </h3>
                  <p class="text-xs text-n-slate-10">
                    {{ t('DOCUMENT_TEMPLATES.CARD.UPDATED') }}
                    {{ formatDate(template.updated_at) }}
                  </p>
                </div>
              </div>

              <div class="relative shrink-0" @click.stop>
                <Button
                  variant="ghost"
                  color="slate"
                  icon="i-lucide-more-vertical"
                  size="sm"
                  @click="toggleMenu(template.id)"
                />
                <DropdownMenu
                  v-if="openMenuId === template.id"
                  :menu-items="menuItems()"
                  class="mt-1 top-full ltr:right-0 rtl:left-0"
                  @action="handleAction(template, $event)"
                />
              </div>
            </div>

            <div class="flex gap-2 items-center mt-4">
              <Badge color="gray" size="small">v{{ template.version }}</Badge>
            </div>
          </div>
        </div>

        <p
          v-if="templates.length && !filteredTemplates.length"
          class="py-8 text-sm text-center text-n-slate-10"
        >
          {{ t('DOCUMENT_TEMPLATES.NO_RESULTS') }}
        </p>
      </div>
    </template>
  </SettingsLayout>

  <!-- Preview modal -->
  <div
    v-if="showPreview"
    class="flex fixed inset-0 z-50 justify-center items-center p-6 bg-black/50"
    @click.self="showPreview = false"
  >
    <div
      class="flex flex-col w-full max-w-4xl rounded-xl shadow-xl bg-n-solid-1 max-h-[90vh]"
    >
      <div
        class="flex justify-between items-center px-4 h-12 border-b border-n-weak"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ previewName || t('DOCUMENT_TEMPLATES.PREVIEW.TITLE') }}
        </h3>
        <Button
          variant="ghost"
          color="slate"
          icon="i-lucide-x"
          size="sm"
          @click="showPreview = false"
        />
      </div>
      <div class="overflow-auto flex-1 p-4 bg-n-slate-2">
        <div
          v-if="previewLoading"
          class="flex justify-center items-center py-12"
        >
          <span class="animate-spin i-lucide-loader-2 size-6 text-n-slate-10" />
        </div>
        <iframe
          v-else
          :srcdoc="previewHtml"
          class="w-full h-[70vh] bg-white rounded-md border border-n-weak"
          title="preview"
        />
      </div>
    </div>
  </div>
</template>
